/**
 * Módulo de notificações - ícone no topo, painel de avisos e notificação no navegador (push).
 * Consome api/notificacao (GET lista, PATCH marcar lida).
 * Eventos: renda passiva, subscrição, bonificação, dividendos/JCP, textos personalizados.
 */

(function () {
	const baseURL = window.location.origin;
	const urlNotificacoes = `${baseURL}/api/notificacao/`;

	function formatarData(dataStr) {
		if (!dataStr) return "";
		const d = new Date(dataStr);
		return d.toLocaleDateString("pt-BR", {
			day: "2-digit",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	}

	async function fetchNotificacoes(params = {}) {
		const qs = new URLSearchParams(params).toString();
		const url = qs ? `${urlNotificacoes}?${qs}` : urlNotificacoes;
		const response = await fetch(url, {
			credentials: "include",
			headers: { "X-Requested-With": "XMLHttpRequest" },
		});
		if (!response.ok) throw new Error("Erro ao carregar notificações");
		return response.json();
	}

	/** Como fetchNotificacoes, mas em 404 (página fora do intervalo) retorna { results: [], next: null } em vez de lançar. */
	async function fetchNotificacoesPaginated(params = {}) {
		const qs = new URLSearchParams(params).toString();
		const url = qs ? `${urlNotificacoes}?${qs}` : urlNotificacoes;
		const response = await fetch(url, {
			credentials: "include",
			headers: { "X-Requested-With": "XMLHttpRequest" },
		});
		if (response.status === 404) {
			return { results: [], next: null, previous: null, count: 0, total_pages: 0, current_page: params.page || 1 };
		}
		if (!response.ok) throw new Error("Erro ao carregar notificações");
		return response.json();
	}

	/**
	 * Retorna o filtro de carteira para notificações.
	 * Defina window.notificacoesCarteiraFilter = { carteira: 1, carteiras: [2, 3] } para filtrar.
	 * - carteira: id (número) de uma carteira específica → ?carteira=1
	 * - carteiras: ids (array ou string "1,2") para o campo M2M → ?carteiras=1,2
	 */
	function getCarteiraFilter() {
		const f = window.notificacoesCarteiraFilter;
		if (!f || typeof f !== "object") return {};
		return { carteira: f.carteira, carteiras: f.carteiras };
	}

	const PAGE_SIZE = 50;

	/**
	 * Faz 2 requisições (uma com ?carteira=id, outra com ?carteiras=id1,id2), junta os resultados e remove duplicatas por id.
	 * Suporta paginação (page). Retorna { list, hasMore }.
	 * @param {Object} filters - { carteira, carteiras, lida, page }
	 */
	async function fetchNotificacoesList(filters = {}) {
		const { carteira, carteiras, lida, page = 1 } = filters;
		const hasCarteira = carteira != null && carteira !== "";
		const carteirasArr = Array.isArray(carteiras) ? carteiras : (typeof carteiras === "string" && carteiras.trim() ? carteiras.split(",").map((s) => s.trim()).filter(Boolean) : []);
		const hasCarteiras = carteirasArr.length > 0;
		const baseParams = { page_size: PAGE_SIZE, page };
		if (lida !== undefined && lida !== null) baseParams.lida = lida;

		if (!hasCarteira && !hasCarteiras) {
			const data = await fetchNotificacoesPaginated(baseParams);
			const list = data.results || [];
			const hasMore = !!(data.next != null || (data.total_pages != null && page < data.total_pages));
			return { list, hasMore };
		}

		// Quando temos ambos (ex.: mesmo id da carteira), usar só carteira para evitar 404 em carteiras
		const promises = [];
		if (hasCarteira) promises.push(fetchNotificacoesPaginated({ ...baseParams, carteira: Number(carteira) }));
		if (hasCarteiras && !hasCarteira) promises.push(fetchNotificacoesPaginated({ ...baseParams, carteiras: carteirasArr.join(",") }));

		const results = await Promise.all(promises);
		const byId = new Map();
		let hasMore = false;
		for (const data of results) {
			if (data.next != null || (data.total_pages != null && page < data.total_pages)) hasMore = true;
			const list = data.results || [];
			for (const n of list) {
				if (!byId.has(n.id)) byId.set(n.id, n);
			}
		}
		const merged = Array.from(byId.values());
		merged.sort((a, b) => new Date(b.data_criacao || 0) - new Date(a.data_criacao || 0));
		return { list: merged, hasMore };
	}

	async function marcarComoLida(id) {
		const response = await fetch(`${urlNotificacoes}${id}/`, {
			method: "PATCH",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				"X-CSRFToken": getCsrfToken(),
				"X-Requested-With": "XMLHttpRequest",
			},
			body: JSON.stringify({ lida: true }),
		});
		return response.ok;
	}

	function getCsrfToken() {
		const name = "csrftoken";
		let token = document.querySelector(`[name=csrfmiddlewaretoken]`)?.value;
		if (token) return token;
		const cookies = document.cookie.split(";");
		for (let i = 0; i < cookies.length; i++) {
			const c = cookies[i].trim();
			if (c.indexOf(name + "=") === 0) return c.substring(name.length + 1);
		}
		return "";
	}

	let stateNotificacoes = { list: [], hasMore: false, loadingMore: false, nextPage: 2 };

	function renderPanel(container, list) {
		if (!container) return;
		// Usar sempre a lista do state para refletir carregamento paginado e marcações
		const listToRender = list != null ? list : stateNotificacoes.list;
		const listEl = container.querySelector("[js-notificacoes-lista]");
		const emptyEl = container.querySelector("[js-notificacoes-vazio]");
		const loadingEl = container.querySelector("[js-notificacoes-loading]");
		const loadingMoreEl = container.querySelector("[js-notificacoes-loading-more]");
		const badgeEl = container.querySelector("[js-notificacoes-badge]");
		const naoLidas = listToRender.filter((n) => n.lida !== true && n.lida !== "true");
		if (badgeEl) {
			badgeEl.textContent = naoLidas.length > 0 ? (naoLidas.length > 99 ? "99+" : naoLidas.length) : "";
			badgeEl.classList.toggle("hidden", naoLidas.length === 0);
		}
		if (loadingEl) loadingEl.classList.add("hidden");
		if (listEl) listEl.classList.remove("hidden");
		if (loadingMoreEl) loadingMoreEl.classList.toggle("hidden", !stateNotificacoes.loadingMore);
		// No dropdown só exibimos as não lidas; ao marcar como lida o item sai da lista
		const listarNoDropdown = naoLidas;
		if (emptyEl) emptyEl.classList.toggle("hidden", listarNoDropdown.length > 0);
		if (!listEl) return;
		listEl.innerHTML = listarNoDropdown
			.map(
				(n) => `
			<li class="notificacao-item ${n.lida ? "notificacao-lida" : ""}" data-id="${n.id}">
				<div class="notificacao-item-header">
					<strong class="notificacao-titulo">${escapeHtml(n.titulo)}</strong>
					<span class="notificacao-data">${formatarData(n.data_criacao)}</span>
				</div>
				<p class="notificacao-descricao">${escapeHtml(n.descricao)}</p>
				${!n.lida ? '<button type="button" class="notificacao-marcar-lida">Marcar como lida</button>' : ""}
			</li>
		`
			)
			.join("");
		listEl.querySelectorAll(".notificacao-marcar-lida").forEach((btn) => {
			btn.addEventListener("click", async (e) => {
				const item = e.target.closest(".notificacao-item");
				const id = item && item.dataset.id;
				if (!id) return;
				const ok = await marcarComoLida(id);
				if (ok) {
					const idx = stateNotificacoes.list.findIndex((n) => String(n.id) === id);
					if (idx >= 0) stateNotificacoes.list[idx].lida = true;
					document.querySelectorAll("[js-notificacoes-painel]").forEach((c) => renderPanel(c, stateNotificacoes.list));
				}
			});
		});
	}

	function updateAllPanels(list, opts = {}) {
		if (list != null) stateNotificacoes.list = list;
		if (opts.hasMore !== undefined) stateNotificacoes.hasMore = opts.hasMore;
		if (opts.loadingMore !== undefined) stateNotificacoes.loadingMore = opts.loadingMore;
		if (opts.nextPage !== undefined) stateNotificacoes.nextPage = opts.nextPage;
		document.querySelectorAll("[js-notificacoes-painel]").forEach((c) => renderPanel(c, stateNotificacoes.list));
	}

	function setLoadingAll(loading) {
		document.querySelectorAll("[js-notificacoes-loading]").forEach((el) => el.classList.toggle("hidden", !loading));
		document.querySelectorAll("[js-notificacoes-lista]").forEach((el) => el.classList.toggle("hidden", loading));
		document.querySelectorAll("[js-notificacoes-vazio]").forEach((el) => el.classList.toggle("hidden", true));
	}

	function setLoadingMore(loading) {
		document.querySelectorAll("[js-notificacoes-loading-more]").forEach((el) => el.classList.toggle("hidden", !loading));
	}

	async function atualizarBadge() {
		try {
			const filter = getCarteiraFilter();
			const hasFilter = (filter.carteira != null && filter.carteira !== "") || (Array.isArray(filter.carteiras) && filter.carteiras.length > 0);
			let count = 0;
			if (hasFilter) {
				// Uma requisição com page_size=1 só para pegar o total (data.count) da API
				const params = { page_size: 1, lida: "false" };
				if (filter.carteira != null && filter.carteira !== "") params.carteira = Number(filter.carteira);
				else if (Array.isArray(filter.carteiras) && filter.carteiras.length > 0) params.carteiras = filter.carteiras.join(",");
				const data = await fetchNotificacoesPaginated(params);
				count = data.count != null ? data.count : 0;
			} else {
				const data = await fetchNotificacoes({ page_size: 1, lida: "false" });
				count = data.count != null ? data.count : 0;
			}
			document.querySelectorAll("[js-notificacoes-badge]").forEach((badgeEl) => {
				badgeEl.textContent = count > 0 ? (count > 99 ? "99+" : count) : "";
				badgeEl.classList.toggle("hidden", count === 0);
			});
		} catch (_) {}
	}

	function criarPainelNotificacoes(container) {
		if (!container) return;
		const dropdown = container.closest("[js-dropdown-notificacao]");
		const SCROLL_THRESHOLD = 120;

		async function carregarMais() {
			if (stateNotificacoes.loadingMore || !stateNotificacoes.hasMore) return;
			const filter = getCarteiraFilter();
			stateNotificacoes.loadingMore = true;
			updateAllPanels(null, { loadingMore: true });
			try {
				const { list: newList, hasMore } = await fetchNotificacoesList({
					...filter,
					lida: "false",
					page: stateNotificacoes.nextPage,
				});
				// Usar id como string no Map para evitar duplicata quando API retorna number vs string
				const byId = new Map(stateNotificacoes.list.map((n) => [String(n.id), n]));
				for (const n of newList) {
					const id = String(n.id);
					if (!byId.has(id)) byId.set(id, n);
				}
				const merged = Array.from(byId.values());
				merged.sort((a, b) => new Date(b.data_criacao || 0) - new Date(a.data_criacao || 0));
				stateNotificacoes.list = merged;
				stateNotificacoes.hasMore = hasMore;
				stateNotificacoes.nextPage += 1;
				stateNotificacoes.loadingMore = false;
				// Forçar re-render com a lista merged para o dropdown atualizar
				updateAllPanels(merged, { hasMore, nextPage: stateNotificacoes.nextPage, loadingMore: false });
			} catch (_) {
				stateNotificacoes.loadingMore = false;
				updateAllPanels(null, { loadingMore: false });
			}
		}

		function onScrollList(ev) {
			const el = ev.target;
			if (!el || !stateNotificacoes.hasMore || stateNotificacoes.loadingMore) return;
			const { scrollTop, scrollHeight, clientHeight } = el;
			if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD) {
				carregarMais();
			}
		}

		async function carregar() {
			stateNotificacoes.nextPage = 2;
			setLoadingAll(true);
			try {
				const filter = getCarteiraFilter();
				const { list, hasMore } = await fetchNotificacoesList({ ...filter, lida: "false", page: 1 });
				stateNotificacoes.list = list;
				stateNotificacoes.hasMore = hasMore;
				updateAllPanels(list, { hasMore, nextPage: 2 });
				const listEl = container.querySelector("[js-notificacoes-lista]");
				if (listEl && !listEl.dataset.scrollAttached) {
					listEl.dataset.scrollAttached = "1";
					listEl.addEventListener("scroll", onScrollList);
				}
			} catch (err) {
				document.querySelectorAll("[js-notificacoes-vazio]").forEach((el) => {
					el.textContent = "Não foi possível carregar as notificações.";
					el.classList.remove("hidden");
				});
				document.querySelectorAll("[js-notificacoes-lista]").forEach((el) => el.classList.add("hidden"));
			} finally {
				setLoadingAll(false);
			}
		}

		if (dropdown) {
			let opened = false;
			dropdown.addEventListener("click", (e) => {
				if (!opened && e.target.closest("[js-notificacoes-toggle]")) {
					opened = true;
					carregar();
				}
			});
		}

		window.notificacoesAtualizarBadge = atualizarBadge;
		window.notificacoesCarregarPainel = carregar;
	}

	function escapeHtml(text) {
		if (text == null) return "";
		const div = document.createElement("div");
		div.textContent = text;
		return div.innerHTML;
	}

	function init() {
		document.querySelectorAll("[js-notificacoes-painel]").forEach(criarPainelNotificacoes);
		// Garantir que o dropdown abra no mobile (touch): adicionar "active" ao clicar no toggle
		document.querySelectorAll("[js-notificacoes-toggle]").forEach((toggle) => {
			function openDropdown() {
				const dropdown = toggle.closest("[js-dropdown-notificacao]");
				if (dropdown) {
					dropdown.classList.add("active");
					if (typeof window.notificacoesCarregarPainel === "function") window.notificacoesCarregarPainel();
				}
			}
			toggle.addEventListener("touchstart", openDropdown, { passive: true });
			toggle.addEventListener("click", openDropdown);
		});
		document.querySelectorAll("[js-notificacoes-fechar]").forEach((btn) => {
			btn.addEventListener("click", (e) => {
				e.stopPropagation();
				const dropdown = btn.closest("[js-dropdown-notificacao]");
				if (dropdown) dropdown.classList.remove("active");
			});
		});
		// Ao abrir o modal de notificações (mobile), carregar lista
		document.querySelectorAll("[data-xopen='modal_notificacoes']").forEach((el) => {
			el.addEventListener("click", () => {
				if (typeof window.notificacoesCarregarPainel === "function") window.notificacoesCarregarPainel();
			});
		});
		// Badge já carrega ao entrar na página (número correto desde o início)
		atualizarBadge();
		window.notificacoesAtualizarBadge = atualizarBadge;
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}

	// Notificação no navegador (push local quando há novas não lidas)
	let ultimoCountNaoLidas = null;
	async function verificarEPush() {
		if (!("Notification" in window) || Notification.permission !== "granted") return;
		try {
			const data = await fetchNotificacoes({ page_size: 1, lida: "false" });
			const count = data.count != null ? data.count : 0;
			if (ultimoCountNaoLidas !== null && count > ultimoCountNaoLidas && count > 0) {
				new Notification("Investidor de Verdade", {
					body: count === 1 ? "Você tem 1 nova notificação." : `Você tem ${count} novas notificações.`,
					icon: "/static/front_assets/assets/src/img/logo.svg",
				});
			}
			ultimoCountNaoLidas = count;
		} catch (_) {}
	}

	function solicitarPermissaoPush() {
		if (!("Notification" in window)) return;
		if (Notification.permission === "default") {
			Notification.requestPermission().then(() => {
				if (Notification.permission === "granted") verificarEPush();
			});
		} else if (Notification.permission === "granted") {
			verificarEPush();
		}
	}

	// Atualizar badge ao ganhar foco (volta da aba)
	window.addEventListener("focus", () => {
		const fn = window.notificacoesAtualizarBadge;
		if (typeof fn === "function") fn();
		verificarEPush();
	});

	window.NotificacoesModule = {
		fetchNotificacoes,
		fetchNotificacoesList,
		getCarteiraFilter,
		marcarComoLida,
		solicitarPermissaoPush,
		verificarEPush,
	};
})();
